/**
 * Collections (list), articles (blog), and navigation menus.
 */
import { supabase } from "@/integrations/supabase/client";
import { shopifyRequest } from "./client";
import type { ShopifyArticle, ShopifyCollection, ShopifyMenu } from "./types";

export async function fetchShopifyCollections(
  first: number = 250,
  after?: string | null
): Promise<{
  collections: ShopifyCollection[];
  hasNextPage: boolean;
  endCursor: string | null;
}> {
  const query = `
    query GetCollections($first: Int!, $after: String) {
      collections(first: $first, after: $after) {
        edges {
          node {
            id
            title
            handle
            description
            descriptionHtml
            image {
              url
              altText
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first, after });
    const collections = result.data.collections.edges;
    const hasNextPage = result.data.collections.pageInfo.hasNextPage;
    const endCursor = result.data.collections.pageInfo.endCursor;

    return { collections, hasNextPage, endCursor };
  } catch (error) {
    console.error("Error fetching Shopify collections:", error);
    return { collections: [], hasNextPage: false, endCursor: null };
  }
}

/** Admin-only: fetch ALL collections (including ones not visible to the storefront). */
export async function fetchShopifyAdminCollections(
  first: number = 250,
  after?: string | null
): Promise<{
  collections: ShopifyCollection[];
  hasNextPage: boolean;
  endCursor: string | null;
}> {
  const { data, error } = await supabase.functions.invoke("shopify-admin-collections", {
    body: { first, after: after ?? null },
  });

  if (error) {
    console.error("Error fetching Shopify admin collections:", error);
    throw error;
  }

  if (data?.error) {
    console.error("Shopify admin collections error:", data.error);
    throw new Error(data.message || data.error);
  }

  return {
    collections: (data?.collections ?? []) as ShopifyCollection[],
    hasNextPage: Boolean(data?.hasNextPage),
    endCursor: (data?.endCursor ?? null) as string | null,
  };
}

export async function fetchShopifyArticles(first: number = 10): Promise<ShopifyArticle[]> {
  const query = `
    query GetArticles($first: Int!) {
      articles(first: $first, sortKey: PUBLISHED_AT, reverse: true) {
        edges {
          node {
            id
            title
            handle
            excerpt
            content
            publishedAt
            image {
              url
              altText
            }
            blog {
              title
              handle
            }
            author {
              name
            }
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { first });
    return result.data.articles.edges;
  } catch (error) {
    console.error("Error fetching Shopify articles:", error);
    return [];
  }
}

export async function fetchArticleByHandle(
  blogHandle: string,
  articleHandle: string
): Promise<ShopifyArticle["node"] | null> {
  const query = `
    query GetArticle($blogHandle: String!, $articleHandle: String!) {
      blog(handle: $blogHandle) {
        articleByHandle(handle: $articleHandle) {
          id
          title
          handle
          excerpt
          content
          publishedAt
          image {
            url
            altText
          }
          blog {
            title
            handle
          }
          author {
            name
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { blogHandle, articleHandle });
    return result.data.blog?.articleByHandle || null;
  } catch (error) {
    console.error("Error fetching article:", error);
    return null;
  }
}

export async function fetchShopifyMenus(): Promise<ShopifyMenu[]> {
  const query = `
    query GetMenus {
      menu(handle: "main-menu") {
        id
        handle
        title
        items {
          id
          title
          url
          type
          items {
            id
            title
            url
            type
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, {});
    const menu = result.data?.menu;
    return menu ? [menu] : [];
  } catch (error) {
    console.error("Error fetching Shopify menus:", error);
    return [];
  }
}

export async function fetchShopifyMenuByHandle(handle: string): Promise<ShopifyMenu | null> {
  const query = `
    query GetMenu($handle: String!) {
      menu(handle: $handle) {
        id
        handle
        title
        items {
          id
          title
          url
          type
          items {
            id
            title
            url
            type
          }
        }
      }
    }
  `;

  try {
    const result = await shopifyRequest(query, { handle });
    return result.data?.menu || null;
  } catch (error) {
    console.error("Error fetching Shopify menu:", error);
    return null;
  }
}
